/**
 * useOptimisticUpdates - Hook para atualizações otimistas de mensagens
 * Exibe mensagens imediatamente com status "pending" antes da confirmação do servidor
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  ConversationMessageResponse,
  SendMessageRequest
} from '@troia-v3/types';

interface OptimisticMessage {
  id: string;
  conversationId: string;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  sentAt: string;
  direction: 'outbound';
  content: any;
  plainText?: string;
  isOptimistic?: boolean;
  reactions?: any[];
  isEdited?: boolean;
  replyToMessageId?: string;  // ✅ NOVO: ID da mensagem respondida
  reply?: any;                 // ✅ NOVO: Objeto completo da mensagem respondida
}

/**
 * Hook para gerenciar atualizações otimistas de mensagens
 *
 * @example
 * ```tsx
 * const { addOptimisticMessage, updateOptimisticMessage } = useOptimisticUpdates();
 *
 * // Ao enviar mensagem
 * const tempId = addOptimisticMessage(conversationId, messageData);
 *
 * // Ao receber confirmação
 * updateOptimisticMessage(tempId, serverMessage);
 * ```
 */
export const useOptimisticUpdates = () => {
  const queryClient = useQueryClient();

  /**
   * Adiciona uma mensagem otimista à lista antes da confirmação do servidor
   */
  const addOptimisticMessage = useCallback((
    conversationId: string,
    messageData: SendMessageRequest
  ): string => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const optimisticMessage: OptimisticMessage = {
      id: tempId,
      conversationId,
      status: 'pending',
      sentAt: new Date().toISOString(),
      direction: 'outbound',
      content: messageData.content,
      plainText: (messageData.content.find((c: any) => c.type === 'text') as any)?.text || '',
      isOptimistic: true,
      reactions: [],
      isEdited: false,
      // ✅ NOVO: Copiar reply fields do messageData se existirem
      ...(messageData.replyToMessageId && {
        replyToMessageId: messageData.replyToMessageId,
        reply: (messageData as any).reply  // Campo reply é passado manualmente
      }),
    };

    // ✅ Atualiza queries do infinite scroll (query key: ['messages', 'infinite', conversationId])
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false // Matcha queries com qualquer additionalFilters
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        // Verifica se mensagem já existe no cache
        const messageExists = oldData.pages.some((page: any) =>
          page.items.some((item: any) => item.id === tempId)
        );

        if (messageExists) {
          return oldData;
        }

        // Adiciona mensagem à primeira página (mensagens mais recentes)
        const newPages = [...oldData.pages];
        if (newPages[0]) {
          newPages[0] = {
            ...newPages[0],
            items: [optimisticMessage as ConversationMessageResponse, ...newPages[0].items], // Adiciona no início (ordem descendente do backend)
          };
        }

        return {
          ...oldData,
          pages: newPages,
        };
      }
    );

    return tempId;
  }, [queryClient]);

  /**
   * Atualiza uma mensagem otimista com dados reais do servidor
   */
  const updateOptimisticMessage = useCallback((
    tempId: string,
    serverMessage: ConversationMessageResponse
  ) => {
    const conversationId = serverMessage.conversationId;

    // ✅ Atualiza queries do infinite scroll
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false // Matcha queries com qualquer additionalFilters
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        let foundOptimistic = false;
        let foundRealMessage = false;

        // 1. Verificar se mensagem otimista existe
        oldData.pages.forEach((page: any) => {
          if (page.items.some((msg: any) => msg.id === tempId)) {
            foundOptimistic = true;
          }
          if (page.items.some((msg: any) => msg.id === serverMessage.id)) {
            foundRealMessage = true;
          }
        });

        // 2. Se mensagem real já existe (socket adicionou antes), remover otimista e manter real.
        //    O eco pelo socket traz o payload do evento; a resposta HTTP é o documento
        //    completo — completa os campos em cima do balão que já está na tela.
        if (foundRealMessage) {
          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            items: page.items
              .filter((msg: any) => msg.id !== tempId) // Remove otimista
              .map((msg: any) =>
                msg.id === serverMessage.id ? { ...msg, ...serverMessage, isOptimistic: false } : msg
              ),
          }));
          return { ...oldData, pages: newPages };
        }

        // 3. Se otimista existe, substituir por mensagem real
        if (foundOptimistic) {
          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            items: page.items.map((msg: any) =>
              msg.id === tempId
                ? { ...serverMessage, isOptimistic: false }
                : msg
            ),
          }));
          return { ...oldData, pages: newPages };
        }

        // 4. Se nenhuma encontrada, adicionar mensagem real (edge case raro)
        const newPages = [...oldData.pages];
        if (newPages[0]) {
          newPages[0] = {
            ...newPages[0],
            items: [{ ...serverMessage, isOptimistic: false }, ...newPages[0].items],
          };
        }
        return { ...oldData, pages: newPages };
      }
    );
  }, [queryClient]);

  /**
   * Remove uma mensagem otimista em caso de erro
   */
  const removeOptimisticMessage = useCallback((
    conversationId: string,
    tempId: string
  ) => {
    // ✅ Atualiza queries do infinite scroll
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.filter((msg: any) => msg.id !== tempId),
        }));

        return {
          ...oldData,
          pages: newPages,
        };
      }
    );
  }, [queryClient]);

  /**
   * Marca uma mensagem otimista como falhada
   */
  const markOptimisticMessageFailed = useCallback((
    conversationId: string,
    tempId: string
  ) => {
    // ✅ Atualiza queries do infinite scroll
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((msg: any) =>
            msg.id === tempId
              ? { ...msg, status: 'failed' as const }
              : msg
          ),
        }));

        return {
          ...oldData,
          pages: newPages,
        };
      }
    );
  }, [queryClient]);

  /**
   * Atualiza o status de uma mensagem (pending → sent → delivered → read)
   */
  const updateMessageStatus = useCallback((
    conversationId: string,
    messageId: string,
    status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed'
  ) => {
    // ✅ Atualiza queries do infinite scroll
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((msg: any) =>
            msg.id === messageId
              ? { ...msg, status }
              : msg
          ),
        }));

        return {
          ...oldData,
          pages: newPages,
        };
      }
    );
  }, [queryClient]);

  /**
   * Adiciona uma reação otimista a uma mensagem
   */
  const addOptimisticReaction = useCallback((
    conversationId: string,
    messageId: string,
    emoji: string,
    userId: string
  ) => {
    // ✅ Atualiza queries do infinite scroll
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((msg: any) => {
            if (msg.id !== messageId) return msg;

            const existingReaction = msg.reactions?.find(
              (r: any) => r.userId === userId && r.emoji === emoji
            );

            if (existingReaction) {
              // Remove reação se já existe
              return {
                ...msg,
                reactions: msg.reactions?.filter(
                  (r: any) => !(r.userId === userId && r.emoji === emoji)
                ),
              };
            } else {
              // Adiciona nova reação
              return {
                ...msg,
                reactions: [
                  ...(msg.reactions || []),
                  {
                    emoji,
                    userId,
                    createdAt: new Date().toISOString(),
                  },
                ],
              };
            }
          }),
        }));

        return {
          ...oldData,
          pages: newPages,
        };
      }
    );
  }, [queryClient]);

  /**
   * Atualiza o conteúdo de uma mensagem (para edição)
   */
  const updateOptimisticMessageContent = useCallback((
    conversationId: string,
    messageId: string,
    newContent: Array<{ type: string; text?: string }>,
    newPlainText: string
  ) => {
    // ✅ Atualiza queries do infinite scroll
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((msg: any) =>
            msg.id === messageId
              ? {
                  ...msg,
                  content: newContent,
                  plainText: newPlainText,
                  isEdited: true,
                  editedAt: new Date().toISOString(),
                }
              : msg
          ),
        }));

        return {
          ...oldData,
          pages: newPages,
        };
      }
    );
  }, [queryClient]);

  return {
    addOptimisticMessage,
    updateOptimisticMessage,
    removeOptimisticMessage,
    markOptimisticMessageFailed,
    updateMessageStatus,
    addOptimisticReaction,
    updateOptimisticMessageContent,
  };
};
